import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconActivity,
  IconMicrophone,
  IconSpinner,
  IconSuccess,
  IconVoiceSMS,
} from '../components/icons';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/Card';
import Button from '../components/Button';
import { Label, Input, Textarea, Select } from '../components/Input';
import Alert from '../components/Alert';
import PatientSelector from '../components/PatientSelector';
import { campaignAPI, healthAPI, formatPhoneNumber } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Map patient preferred_language codes → VoiceSMS language values
const LANG_MAP = { en: 'English', tw: 'Twi', ga: 'Ga', ee: 'Ewe', aka: 'Ga', dag: 'Dagbani', fat: 'Fante' };
// Map patient preferred_language codes → local language codes
const LOCAL_LANG_MAP = { tw: 'tw', ga: 'aka', ee: 'ee', dag: 'dag', fat: 'fat' };

// Steps per mode — [label, approx ms before advancing to next step]
const PIPELINE_STEPS = {
  local: [
    { label: 'Generating health message with AI…',  ms: 4000 },
    { label: 'Translating to local language…',       ms: 5000 },
    { label: 'Synthesising speech audio…',           ms: 9000 },
    { label: 'Queuing for review…',                  ms: null  },
  ],
  ai: [
    { label: 'Generating educational content…',     ms: 5000 },
    { label: 'Synthesising speech…',                ms: 8000 },
    { label: 'Queuing for review…',                  ms: null  },
  ],
  custom: [
    { label: 'Synthesising speech…',                ms: 6000 },
    { label: 'Queuing for review…',                 ms: null  },
  ],
};

const PipelineProgress = ({ mode, activeStep }) => {
  const steps = PIPELINE_STEPS[mode] ?? [];
  return (
    <div className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3">
      {steps.map((step, i) => {
        const done    = i < activeStep;
        const active  = i === activeStep;
        const pending = i > activeStep;
        return (
          <div key={i} className="flex items-center space-x-3">
            <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
              {done ? (
                <IconSuccess className="w-5 h-5 text-green-500" />
              ) : active ? (
                <IconSpinner className="w-5 h-5 text-green-600 animate-spin" />
              ) : (
                <div className="w-3 h-3 rounded-full border-2 border-gray-300 mx-auto" />
              )}
            </div>
            <span className={`text-sm ${done ? 'text-green-600 line-through' : active ? 'text-green-800 font-medium' : 'text-gray-400'}`}>
              {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const VoiceSMS = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(null); // null = idle
  const [alert, setAlert] = useState(null);
  const [mode, setMode] = useState('ai'); // 'ai' | 'local' | 'custom'
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const timerRefs = useRef([]);

  const [formData, setFormData] = useState({
    campaignName: '',
    diseaseName: '',
    language: 'English',
    campaignGoals: '',
    customMessage: '',
    localLanguage: 'tw',
    localDiseaseName: '',
    localCampaignGoals: '',
  });

  const languages = ['English', 'Twi', 'Ga', 'Ewe'];
  const localLanguages = [
    { code: 'tw',  name: 'Twi (Akan)' },
    { code: 'ee',  name: 'Ewe' },
    { code: 'aka', name: 'Ga' },
    { code: 'dag', name: 'Dagbani' },
    { code: 'fat', name: 'Fante' },
  ];

  useEffect(() => {
    healthAPI.check()
      .then(() => setConnectionStatus('connected'))
      .catch(() => setConnectionStatus('error'));
  }, []);

  // Clear all pending step timers
  const clearStepTimers = () => {
    timerRefs.current.forEach(clearTimeout);
    timerRefs.current = [];
  };

  // Kick off timed step advancement when loading starts
  const startStepProgress = (currentMode) => {
    const steps = PIPELINE_STEPS[currentMode] ?? [];
    setActiveStep(0);
    let elapsed = 0;
    steps.forEach((step, i) => {
      if (step.ms !== null && i < steps.length - 1) {
        elapsed += step.ms;
        const t = setTimeout(() => setActiveStep(i + 1), elapsed);
        timerRefs.current.push(t);
      }
    });
  };

  // Stop progress (on success or error)
  const stopStepProgress = () => {
    clearStepTimers();
    setActiveStep(null);
  };

  // ── Auto-fill disease + language when patients are selected ──────────────────
  const handlePatientSelect = (patients) => {
    setSelectedPatients(patients);
    if (patients.length === 0) return;

    const diagnoses = [...new Set(patients.map((p) => p.diagnosis).filter(Boolean))];
    if (diagnoses.length === 1) {
      setFormData((prev) => ({ ...prev, diseaseName: diagnoses[0] }));
    }

    const langs = [...new Set(patients.map((p) => p.preferred_language).filter(Boolean))];
    if (langs.length === 1) {
      const code = langs[0];
      const aiLang = LANG_MAP[code];
      if (aiLang) setFormData((prev) => ({ ...prev, language: aiLang }));
      const localCode = LOCAL_LANG_MAP[code];
      if (localCode) setFormData((prev) => ({ ...prev, localLanguage: localCode }));
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    setLoading(true);
    startStepProgress(mode);

    try {
      if (selectedPatients.length === 0) {
        setAlert({ type: 'error', title: 'No patients selected', message: 'Please select at least one patient.' });
        return;
      }

      const recipients = selectedPatients.map((p) => formatPhoneNumber(p.phone));

      let result;

      if (mode === 'ai') {
        result = await campaignAPI.createVoiceCampaign({
          campaign_name: formData.campaignName,
          recipients,
          disease_name: formData.diseaseName,
          language: formData.language,
          campaign_goals: formData.campaignGoals || 'Educate and raise awareness',
          require_review: true,
          user_id: user?.id ?? null,
        });
      } else if (mode === 'local') {
        result = await campaignAPI.createLocalVoiceCampaign({
          campaign_name: formData.campaignName,
          recipients,
          disease_name: formData.localDiseaseName,
          campaign_goals: formData.localCampaignGoals || 'Educate and raise awareness',
          target_language: formData.localLanguage,
          require_review: true,
          user_id: user?.id ?? null,
        });
      } else {
        result = await campaignAPI.testVoiceCall({
          campaign_name: formData.campaignName,
          recipients,
          message: formData.customMessage,
          require_review: true,
        });
      }

      if (result.review_id) {
        navigate(`/review/${result.review_id}`);
        return;
      }

      const successMessage =
        mode === 'local'
          ? `Local language voice calls initiated to ${recipients.length} patient(s) in ${formData.localLanguage}.`
          : `Voice calls initiated to ${recipients.length} patient(s).`;

      setAlert({ type: 'success', title: 'Voice Campaign Created', message: successMessage });

      setFormData({
        campaignName: '',
        diseaseName: '',
        language: 'English',
        campaignGoals: '',
        customMessage: '',
        localLanguage: 'tw',
        localDiseaseName: '',
        localCampaignGoals: '',
      });
      setSelectedPatients([]);
    } catch (error) {
      setAlert({
        type: 'error',
        title: 'Campaign Failed',
        message: error.userMessage || error.response?.data?.detail || error.response?.data?.message || error.message,
      });
    } finally {
      stopStepProgress();
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Voice SMS Campaign</h2>
        <p className="text-gray-600 mt-1">
          Create AI-powered voice message campaigns with natural speech synthesis
        </p>
      </div>

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} />}

      {connectionStatus === 'error' && (
        <Alert
          type="error"
          title="Backend Connection Failed"
          message="Cannot connect to the CareArena backend API."
        />
      )}
      {connectionStatus === 'connected' && (
        <div className="flex items-center space-x-2 text-sm text-green-600">
          <IconActivity className="h-4 w-4" />
          <span>Connected to backend API</span>
        </div>
      )}

      {/* Mode Toggle */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-center flex-wrap gap-4 md:gap-8">
            {[
              { value: 'ai',     label: 'AI-Generated',   note: '(English)' },
              { value: 'local',  label: 'Local Language',  note: '(Twi, Ewe, Ga…)', noteClass: 'text-green-600' },
              { value: 'custom', label: 'Custom Message',  note: '' },
            ].map(({ value, label, note, noteClass }) => (
              <label key={value} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  checked={mode === value}
                  onChange={() => setMode(value)}
                  className="w-4 h-4 text-primary-600"
                />
                <span className="font-medium text-gray-900">{label}</span>
                {note && <span className={`text-xs ${noteClass || 'text-gray-500'}`}>{note}</span>}
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Create Voice Campaign</CardTitle>
              <CardDescription>
                {mode === 'ai'
                  ? 'AI generates educational content and converts it to natural speech'
                  : mode === 'local'
                  ? 'AI generates an English message from your campaign goal, translates it, and converts to speech'
                  : 'Your custom message is converted to natural speech'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Campaign Name */}
                <div>
                  <Label htmlFor="campaignName">Campaign Name *</Label>
                  <Input
                    id="campaignName"
                    name="campaignName"
                    placeholder="e.g., Malaria Prevention Campaign"
                    value={formData.campaignName}
                    onChange={handleChange}
                    required
                  />
                </div>

                {/* Patient Selector */}
                <div>
                  <Label>
                    Patients *{' '}
                    {selectedPatients.length > 0 && (
                      <span className="text-gray-500 font-normal">
                        ({selectedPatients.length} selected)
                      </span>
                    )}
                  </Label>
                  <div className="mt-1">
                    <PatientSelector
                      onSelect={handlePatientSelect}
                      title="Select patients for this voice campaign"
                    />
                  </div>
                  {selectedPatients.length > 0 && (formData.diseaseName || formData.language !== 'English') && (
                    <p className="text-xs text-primary-600 mt-1">
                      Auto-filled from patient data —{' '}
                      {formData.diseaseName && `topic: "${formData.diseaseName}"`}
                      {formData.diseaseName && formData.language !== 'English' ? ', ' : ''}
                      {formData.language !== 'English' && `language: ${formData.language}`}
                    </p>
                  )}
                </div>

                {/* Mode-specific fields */}
                {mode === 'ai' ? (
                  <>
                    <div>
                      <Label htmlFor="diseaseName">Health Topic *</Label>
                      <Input
                        id="diseaseName"
                        name="diseaseName"
                        placeholder="e.g., Malaria Prevention, Preeclampsia, Mental Health"
                        value={formData.diseaseName}
                        onChange={handleChange}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-filled when all selected patients share the same diagnosis.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="language">Language *</Label>
                      <Select
                        id="language"
                        name="language"
                        value={formData.language}
                        onChange={handleChange}
                        required
                      >
                        {languages.map((lang) => (
                          <option key={lang} value={lang}>{lang}</option>
                        ))}
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="campaignGoals">
                        Campaign Goals{' '}
                        <span className="text-gray-500 font-normal">(Optional)</span>
                      </Label>
                      <Textarea
                        id="campaignGoals"
                        name="campaignGoals"
                        placeholder="e.g., Educate about prevention methods, Increase awareness of symptoms"
                        rows={3}
                        value={formData.campaignGoals}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                ) : mode === 'local' ? (
                  <>
                    <div>
                      <Label htmlFor="localLanguage">Target Language *</Label>
                      <Select
                        id="localLanguage"
                        name="localLanguage"
                        value={formData.localLanguage}
                        onChange={handleChange}
                        required
                      >
                        {localLanguages.map((l) => (
                          <option key={l.code} value={l.code}>{l.name}</option>
                        ))}
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        Auto-filled when all selected patients share the same language preference.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="localDiseaseName">Health Topic *</Label>
                      <Input
                        id="localDiseaseName"
                        name="localDiseaseName"
                        placeholder="e.g., Malaria Prevention, Hypertension, Maternal Health"
                        value={formData.localDiseaseName}
                        onChange={handleChange}
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        AI will generate an English message about this topic, then translate it.
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="localCampaignGoals">
                        Campaign Goals{' '}
                        <span className="text-gray-500 font-normal">(Optional)</span>
                      </Label>
                      <Textarea
                        id="localCampaignGoals"
                        name="localCampaignGoals"
                        placeholder="e.g., Educate about prevention methods, Increase awareness of early symptoms"
                        rows={3}
                        value={formData.localCampaignGoals}
                        onChange={handleChange}
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <Label htmlFor="customMessage">
                      Voice Message *{' '}
                      <span className="text-gray-500 font-normal">
                        ({formData.customMessage.split(' ').filter(Boolean).length} words)
                      </span>
                    </Label>
                    <Textarea
                      id="customMessage"
                      name="customMessage"
                      placeholder="Enter the message you want to convert to speech..."
                      rows={6}
                      value={formData.customMessage}
                      onChange={handleChange}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Keep under 150 words for optimal voice delivery (1–2 minutes).
                    </p>
                  </div>
                )}

                {/* Progressive loading steps */}
                {loading && activeStep !== null && (
                  <PipelineProgress mode={mode} activeStep={activeStep} />
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  loading={loading}
                  disabled={selectedPatients.length === 0 || loading}
                  className="w-full"
                >
                  <IconVoiceSMS className="h-5 w-5 mr-2" />
                  {mode === 'ai'
                    ? 'Generate & Send Voice Campaign'
                    : mode === 'local'
                    ? 'Translate & Send Local Voice'
                    : 'Convert & Send Voice Message'}
                  {selectedPatients.length > 0 && ` (${selectedPatients.length} patients)`}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Info Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">How it Works</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              {mode === 'ai' && (
                <>
                  {[
                    'Select patients — disease and language are auto-filled',
                    'AI generates educational content',
                    'Content is converted to natural speech',
                    'Voice calls are delivered to all patients',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </>
              )}
              {mode === 'local' && (
                <>
                  {[
                    'Select patients — target language is auto-filled from patient preferences',
                    'Enter health topic and campaign goals',
                    'AI generates an English health message',
                    'Message is translated to the local language (Twi, Ewe, Ga…)',
                    'Translated text is converted to speech and delivered',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </>
              )}
              {mode === 'custom' && (
                <>
                  {[
                    'Select patients from your database',
                    'Your message is converted to natural speech',
                    'Voice calls are delivered to all patients',
                  ].map((step, i) => (
                    <div key={i} className="flex items-start space-x-3">
                      <div className="w-6 h-6 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </div>
                      <p className="text-gray-700">{step}</p>
                    </div>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default VoiceSMS;
